/**
 * Ruch z kodu — jedna bramka dla całej aplikacji.
 *
 * Reguła `prefers-reduced-motion` z reset.css wyłącza animacje CSS, ale
 * **nie dotyczy** `element.animate()`. Gdyby każdy widok wołał WAAPI
 * bezpośrednio, ustawienie systemowe działałoby w połowie aplikacji — a to
 * gorzej, niż gdyby nie działało wcale, bo nikt by tego nie zauważył.
 *
 * Kontrakt jest ważniejszy od kodu: **wolno tu wołać wyłącznie rzeczy, które
 * już się wydarzyły.** Jeśli coś czeka na koniec animacji, żeby się stać, to
 * przy wyłączonym ruchu nie stanie się nigdy.
 */

const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');

/** @returns {boolean} czy użytkownik prosi o ograniczenie ruchu */
export function prefersReducedMotion() {
  return Boolean(reduced?.matches);
}

/**
 * Animuje element, o ile wolno.
 *
 * @param {Element | null | undefined} element
 * @param {Keyframe[]} keyframes
 * @param {KeyframeAnimationOptions} [options]
 * @returns {Animation | null} `null`, gdy ruch jest wyłączony albo nie ma czego animować
 */
export function animate(element, keyframes, options = {}) {
  if (prefersReducedMotion() || !element?.animate) return null;

  try {
    return element.animate(keyframes, {
      easing: 'cubic-bezier(0.2, 0, 0.2, 1)',
      fill: 'none',
      ...options,
    });
  } catch {
    // Animacja jest dodatkiem — jej awaria nie może przerwać rozgrywki.
    return null;
  }
}

/**
 * Przebieg od 0 do 1 w czasie — podstawa dla wszystkiego, co zmienia
 * **treść**, a nie wygląd, więc nie może pójść przez WAAPI.
 *
 * Liniowo, bez wytracania pędu: liczby w tej aplikacji są jednocyfrowe,
 * a przy krzywej zwalniającej wartość docelowa pojawiała się po dwóch
 * dziesiątych sekundy — doliczania nie było wtedy widać wcale.
 *
 * @param {(progress: number) => void} onStep wołane z wartością 0…1
 * @param {number} [duration]
 * @returns {() => void} przerwanie; domyka przebieg na 1
 */
export function ramp(onStep, duration = 600) {
  if (prefersReducedMotion()) {
    onStep(1);
    return () => {};
  }

  const start = window.performance.now();
  let frame = requestAnimationFrame(function step(now) {
    const progress = Math.min(1, (now - start) / duration);
    onStep(progress);
    if (progress < 1) frame = requestAnimationFrame(step);
  });

  return () => {
    cancelAnimationFrame(frame);
    onStep(1);
  };
}

/**
 * Dolicza liczbę od zera do wartości docelowej.
 *
 * Kończy dokładnie na wartości z API, nigdy na zaokrągleniu klatki.
 *
 * @param {number} to wartość docelowa
 * @param {(value: number) => void} onStep wołane z wartością całkowitą
 * @param {number} [duration]
 * @returns {() => void} przerwanie; ustawia wartość docelową natychmiast
 */
export function countUp(to, onStep, duration = 600) {
  if (!Number.isFinite(to)) {
    onStep(to);
    return () => {};
  }

  return ramp((progress) => onStep(Math.round(to * progress)), duration);
}

/**
 * Zwija wiersz, zanim zniknie z listy.
 *
 * W liście pięćdziesięciu pozycji samo zniknięcie nie mówi, **który** wiersz
 * zniknął — reszta po prostu podskakuje. Zwinięcie pokazuje miejsce.
 *
 * Wysokość mierzymy przed animacją, bo `height: auto` nie jest animowalne.
 * Wołający czeka na obietnicę i dopiero potem przeładowuje listę.
 *
 * @param {Element | null | undefined} element
 * @returns {Promise<void>}
 */
export function collapse(element) {
  if (!element) return Promise.resolve();

  const { offsetHeight } = element;
  element.style.overflow = 'hidden';

  const animation = animate(
    element,
    [
      { height: `${offsetHeight}px`, opacity: 1 },
      { height: '0px', opacity: 0, paddingTop: '0px', paddingBottom: '0px', marginBottom: '0px' },
    ],
    { duration: 220, fill: 'forwards' },
  );

  if (!animation) return Promise.resolve();
  return animation.finished.catch(() => {});
}
